import { CssBaseline, GlobalStyles } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { createAppTheme } from "./createAppTheme";
import { thirdPartyStyles } from "./globalStyles";
import { palettes } from "./palette";
import { APP_THEME_SPECS, DEFAULT_APP_THEME_ID, getThemeSpec, type AppThemeId, type AppThemeSpec } from "./themeSpecs";
import { themeVars } from "./tokens";

export const THEME_STORAGE_PREFIX = "flownect.theme";
export const LAST_THEME_STORAGE_KEY = `${THEME_STORAGE_PREFIX}.last`;

export const storageKeyForUser = (userId?: string | null) =>
  userId ? `${THEME_STORAGE_PREFIX}.${userId}` : LAST_THEME_STORAGE_KEY;

type AppThemeContextValue = {
  themeId: AppThemeId;
  spec: AppThemeSpec;
  themes: AppThemeSpec[];
  mode: "light" | "dark";
  isDark: boolean;
  setThemeId: (id: AppThemeId) => void;
};

const AppThemeContext = createContext<AppThemeContextValue | null>(null);

const readStoredTheme = (key: string): AppThemeId | null => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored ? getThemeSpec(stored).id : null;
  } catch {
    return null;
  }
};

const writeStoredTheme = (key: string, id: AppThemeId) => {
  try {
    window.localStorage.setItem(key, id);
    window.localStorage.setItem(LAST_THEME_STORAGE_KEY, id);
  } catch {
    return;
  }
};

export const AppThemeProvider = ({ children }: { children: ReactNode }) => {
  const userId = useSelector((state: { userState?: { uid?: string } }) => state.userState?.uid) || null;

  const [themeId, setThemeIdState] = useState<AppThemeId>(
    () => readStoredTheme(LAST_THEME_STORAGE_KEY) ?? DEFAULT_APP_THEME_ID,
  );

  useEffect(() => {
    if (!userId) return;
    const stored = readStoredTheme(storageKeyForUser(userId));
    setThemeIdState(stored ?? DEFAULT_APP_THEME_ID);
  }, [userId]);

  const setThemeId = useCallback(
    (id: AppThemeId) => {
      const next = getThemeSpec(id).id;
      setThemeIdState(next);
      writeStoredTheme(storageKeyForUser(userId), next);
    },
    [userId],
  );

  const spec = getThemeSpec(themeId);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-app-theme", spec.id);
    root.setAttribute("data-color-mode", spec.mode);
    root.style.colorScheme = spec.mode;
    root.style.backgroundColor = palettes[spec.id].surface.page;
  }, [spec.id, spec.mode]);

  const theme = useMemo(() => createAppTheme(spec.id), [spec.id]);

  const value = useMemo<AppThemeContextValue>(
    () => ({
      themeId: spec.id,
      spec,
      themes: APP_THEME_SPECS,
      mode: spec.mode,
      isDark: spec.mode === "dark",
      setThemeId,
    }),
    [spec, setThemeId],
  );

  const globalTokens = useMemo(() => {
    const blocks: Record<string, Record<string, string>> = {};
    APP_THEME_SPECS.forEach((entry) => {
      const vars = {
        ...themeVars[entry.id],
        "--fx-icon-filter": entry.mode === "dark" ? "invert(1) hue-rotate(180deg)" : "none",
        colorScheme: entry.mode,
      };
      blocks[entry.id === DEFAULT_APP_THEME_ID ? ":root" : `html[data-app-theme="${entry.id}"]`] = vars;
    });
    blocks[`html[data-app-theme="${DEFAULT_APP_THEME_ID}"]`] = blocks[":root"];
    return { ...blocks, ...thirdPartyStyles };
  }, []);

  return (
    <AppThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <GlobalStyles styles={globalTokens} />
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};

export const useAppTheme = (): AppThemeContextValue => {
  const context = useContext(AppThemeContext);
  if (!context) throw new Error("useAppTheme must be used inside AppThemeProvider");
  return context;
};

export const useColorMode = useAppTheme;

export const useRawPalette = () => palettes[useAppTheme().themeId];
